'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { Activity, Bell, TrendingUp } from 'lucide-react'
import { EASE, SPRING } from '@/lib/motion'

// The social-listening hero, animated as the job it does: the monthly
// mention count builds, the trend chip settles, then the spike alert
// POPS in on a spring (the product's whole pitch in one beat: you'd
// have missed this), and the keyword treemap blooms tile by tile so
// share-of-voice reads as measured. Plays once on scroll-in; reduced
// motion resolves to the final dashboard instantly.

const TREEMAP = [
  { k: 'Glow',      v: 18, tone: 'bg-indigo-900 text-indigo-50', col: 'col-span-3' },
  { k: 'Beauty',    v: 10, tone: 'bg-violet-700 text-violet-50', col: 'col-span-2' },
  { k: 'Routine',   v: 6,  tone: 'bg-violet-500 text-violet-50', col: 'col-span-1' },
  { k: 'Skincare',  v: 18, tone: 'bg-indigo-900 text-indigo-50', col: 'col-span-3' },
  { k: 'Hydration', v: 9,  tone: 'bg-violet-700 text-violet-50', col: 'col-span-2' },
  { k: 'Lipstick',  v: 4,  tone: 'bg-violet-400 text-violet-50', col: 'col-span-1' },
  { k: 'Allergy',   v: 16, tone: 'bg-indigo-800 text-indigo-50', col: 'col-span-3' },
  { k: 'Mask',      v: 8,  tone: 'bg-violet-600 text-violet-50', col: 'col-span-2' },
  { k: 'Serums',    v: 3,  tone: 'bg-violet-400 text-violet-50', col: 'col-span-1' },
]

const FOOTER = [
  { label: 'Top author',    value: '@beautylover', mono: false },
  { label: 'Sentiment',     value: '+0.62',        mono: true },
  { label: 'Most-engaging', value: 'IG · 176k',    mono: false },
]

export function ListeningPulseMotion() {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="relative">
        <div className="absolute -inset-6 -z-10 rounded-[2.25rem] bg-gradient-to-br from-violet-500/10 via-transparent to-rose-200/20 blur-2xl" />

        <m.div
          className="overflow-hidden rounded-3xl border bg-card shadow-2xl shadow-violet-900/10"
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.5, ease: EASE }}
        >
          <div className="flex items-center justify-between border-b bg-secondary/40 px-5 py-3">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              <span className="text-[11px] font-medium uppercase tracking-wider text-foreground">Social listening · live</span>
            </div>
            <span className="rounded-full bg-card px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              3 queries · 2 running
            </span>
          </div>

          <div className="grid gap-4 p-5 md:grid-cols-[1fr_auto] md:items-start">
            <div>
              <p className="font-display text-3xl font-bold tracking-tight tabular-nums">
                <ListeningCount inView={inView} />
              </p>
              <p className="text-[11px] text-muted-foreground">mentions · this month</p>
              <m.div
                className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-700"
                initial={reduce ? false : { opacity: 0, y: 6 }}
                animate={inView ? { opacity: 1, y: 0 } : undefined}
                transition={{ duration: 0.35, ease: EASE, delay: reduce ? 0 : 0.8 }}
              >
                <TrendingUp className="h-3 w-3" /> +26%
              </m.div>
            </div>
            {/* The beat that sells the product: the spike you would
                have missed, landing on a spring after the count. */}
            <m.div
              className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2"
              initial={reduce ? false : { opacity: 0, scale: 0.9, y: 8 }}
              animate={inView ? { opacity: 1, scale: 1, y: 0 } : undefined}
              transition={reduce ? undefined : { ...SPRING, delay: 1.05 }}
            >
              <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                {inView && !reduce && (
                  <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400/60 motion-safe:animate-ping" />
                )}
                <Bell className="relative h-3.5 w-3.5 text-rose-600" />
              </span>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Spike alert · 2h</p>
                <p className="text-[11px] text-rose-900">+1,496 mentions · #creamallergy</p>
              </div>
            </m.div>
          </div>

          <div className="border-t bg-secondary/20 p-4">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Top keywords</p>
            <ul className="mt-2 grid grid-cols-6 gap-1.5">
              {TREEMAP.map((t, i) => (
                <m.li
                  key={t.k}
                  className={`flex flex-col justify-end rounded-md p-2 ${t.tone} ${t.col}`}
                  style={{ minHeight: 56 }}
                  initial={reduce ? false : { opacity: 0, scale: 0.94 }}
                  animate={inView ? { opacity: 1, scale: 1 } : undefined}
                  transition={{ duration: 0.32, ease: EASE, delay: reduce ? 0 : 0.35 + i * 0.06 }}
                >
                  <p className="text-[11px] font-bold leading-tight">{t.k}</p>
                  <p className="text-[9px] opacity-80">{t.v}%</p>
                </m.li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-3 divide-x border-t text-[10px]">
            {FOOTER.map((f, i) => (
              <m.div
                key={f.label}
                className="px-3 py-2.5"
                initial={reduce ? false : { opacity: 0 }}
                animate={inView ? { opacity: 1 } : undefined}
                transition={{ duration: 0.4, ease: EASE, delay: reduce ? 0 : 1.2 + i * 0.08 }}
              >
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{f.label}</p>
                <p className={f.mono ? 'font-display text-sm font-semibold text-foreground' : 'truncate font-medium text-foreground'}>
                  {f.value}
                </p>
              </m.div>
            ))}
          </div>
        </m.div>
      </div>
    </LazyMotion>
  )
}

// The headline figure counts up over the first second so the volume
// reads as monitored, not typeset. Locale-formatted along the way.
function ListeningCount({ inView }: { inView: boolean }) {
  const reduce = useReducedMotion()
  if (reduce || !inView) return <>{(23_567).toLocaleString()}</>
  return <AnimatedLocaleNumber value={23_567} />
}

function AnimatedLocaleNumber({ value }: { value: number }) {
  const [display, setDisplay] = React.useState(0)
  React.useEffect(() => {
    let raf: number
    const start = performance.now()
    const dur = 1000
    const tick = (now: number) => {
      const t = Math.min((now - start) / dur, 1)
      // Same curve family as EASE: fast start, long settle.
      const eased = 1 - Math.pow(1 - t, 3)
      setDisplay(Math.round(value * eased))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value])
  return <>{display.toLocaleString()}</>
}
